import {ROLE_OPTIONS} from 'constants/roleOptions';
import {RoleType} from 'graphql/types/AccountType';
import { useTranslation } from 'react-i18next';
import { getSmartLabelClassName } from '../../../../utils/labelWidthUtils';
import InputFilterItem from '../../../delivery/components/InputFilterItem';
import ParcelFilterStyle from '../../../delivery/components/ParcelFilterStyle';

export interface FilterOptions {
    role: RoleType | 'all';
    id: string;
    name: string;
    phone: string;
    status: number;
}

interface StatusOption {
    value: number;
    labelEn: string;
    labelEs: string;
    labelZh: string;
}

export const STATUS_OPTIONS: StatusOption[] = [
    {value: 2, labelEn: 'All', labelEs: 'Todos', labelZh: '全部'},
    {value: 1, labelEn: 'Active', labelEs: 'Activo', labelZh: '启用'},
    {value: 0, labelEn: 'Inactive', labelEs: 'Inactivo', labelZh: '禁用'},
];

interface FilterAreaProps {
    filters: FilterOptions;
    setFilters: React.Dispatch<React.SetStateAction<FilterOptions>>;
}

export const DEFAULT_FILTER_OPTIONS: FilterOptions = {
    role: 'all',
    id: '',
    name: '',
    phone: '',
    status: 2,
};

const FilterArea: React.FC<FilterAreaProps> = ({ filters, setFilters }) => {
    const { t, i18n } = useTranslation();

    const roleOptions = ROLE_OPTIONS.map(option => ({
        value: option.value,
        label: option.value === 'all' ? t('enum.role.all') : t(`enum.role.${option.value}`)
    }));

    const currentLang = i18n.language;
    const statusOptions = STATUS_OPTIONS.map(option => {
        let label = '';
        switch (currentLang) {
            case 'zh':
            case 'zh-CN':
                label = option.labelZh;
                break;
            case 'es':
                label = option.labelEs;
                break;
            case 'en':
            default:
                label = option.labelEn;
                break;
        }
        return { value: option.value, label };
    });
    const statusOptionsForSelect = statusOptions.map(opt => ({
        value: String(opt.value),
        label: opt.label
    }));

    const column1Labels = [t('central.personnel.filters.role'), t('central.personnel.filters.phone')];
    const column2Labels = [t('central.personnel.filters.personnelId'), t('central.personnel.filters.status')];
    const column3Labels = [t('central.personnel.filters.name')];

    const handleFilterChange = (name: string, value: any) => {
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    return (
        <div className="mb-2 pl-0 py-2">
            <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('central.personnel.filters.role')}
                        options={roleOptions}
                        value={filters.role}
                        onChange={(value: string) => handleFilterChange('role', (value || 'all') as RoleType)}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column1Labels)}
                        placeholder={t('central.personnel.placeholders.role') || undefined}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={t('central.personnel.filters.personnelId')}
                        value={filters.id}
                        onChange={(e) => handleFilterChange('id', e.target.value)}
                        spanClassName={getSmartLabelClassName(column2Labels)}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={t('central.personnel.filters.name')}
                        value={filters.name}
                        onChange={(e) => handleFilterChange('name', e.target.value)}
                        spanClassName={getSmartLabelClassName(column3Labels)}
                    />
                </div>

                <div className="min-w-0">
                    <InputFilterItem
                        label={t('central.personnel.filters.phone')}
                        value={filters.phone}
                        onChange={(e) => handleFilterChange('phone', e.target.value)}
                        spanClassName={getSmartLabelClassName(column1Labels)}
                    />
                </div>

                <div className="min-w-0">
                    <ParcelFilterStyle
                        label={t('central.personnel.filters.status')}
                        options={statusOptionsForSelect}
                        value={String(filters.status)}
                        onChange={(value: string) => handleFilterChange('status', value ? Number(value) : 2)}
                        className="w-full"
                        spanClassName={getSmartLabelClassName(column2Labels)}
                        placeholder={t('components.input.all') || undefined}
                    />
                </div>
            </div>
        </div>
    );
};

export default FilterArea;
